import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';
import Variables from '../../design-system/variables';
import Colours from '../../design-system/colours';
import OvPageTitle from '../UI/molecules/OvPageTitle';
import PollIcon from '@mui/icons-material/Poll';
import OvToggle from '../UI/atoms/OvToggle';
import { NpsSurveyTabType } from '../../common/model/type/nps-survey-tab.type';
import { Tab, Tabs } from '@mui/material';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useRouteUrl } from '../../hooks/use-route-url';
import { last } from 'lodash';
import OvNpsSurveySetupTab from '../UI/organisms/OvNpsSurveySetupTab';
import OvNpsSurveyDashboardTab from '../UI/organisms/OvNpsSurveyDashboardTab';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import { useAdminNpsSurveySettings } from '../../hooks/use-admin-nps-survey-settings';
import { useAdminCurrentNpsSurvey } from '../../hooks/use-admin-current-nps-survey';
import { getAxiosErrorMessage } from '../../common/utils/helpers';

const NpsSurvey: FC = () => {
  const { t } = useTranslation();
  const url = useRouteUrl();
  let location = useLocation();
  const { settings, updateSettings } = useAdminNpsSurveySettings();
  const currentNpsSurvey = useAdminCurrentNpsSurvey();
  const surveySettings = settings.data;
  const isLoading = settings.isFetching || updateSettings.isPending;
  const error =
    settings.error ?? currentNpsSurvey.error ?? updateSettings.error;
  const [activeTab, setActiveTab] = useState<NpsSurveyTabType>(
    NpsSurveyTabType.SETUP
  );

  const handleNpsSurveyEnabledChange = (enabled: boolean) => {
    if (surveySettings) {
      updateSettings.mutate({ ...surveySettings, enabled });
    }
  };

  useEffect(() => {
    const pathName = last(location.pathname.split('/')) as NpsSurveyTabType;

    if (Object.values(NpsSurveyTabType).includes(pathName)) {
      setActiveTab(pathName);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Container>
      <OvPageTitle
        title={t('survey.npsSurvey')}
        icon={<StyledPollIcon />}
        infoTooltip={t('survey.describeNps')}
      >
        <OvToggle
          label={t('common.status')}
          toggleOnLabel={t('common.enabled')}
          toggleOffLabel={t('common.disabled')}
          value={!!surveySettings?.enabled}
          disabled={isLoading}
          onChange={handleNpsSurveyEnabledChange}
        />
      </OvPageTitle>

      <TabContainer>
        <Tabs
          onChange={(event: any, newValue: NpsSurveyTabType) => {
            setActiveTab(newValue);
          }}
          value={activeTab}
        >
          <Tab
            style={{
              borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
              textTransform: 'none',
            }}
            value={NpsSurveyTabType.SETUP}
            label={t('survey.setup')}
            component={Link}
            to={`${url}/${NpsSurveyTabType.SETUP}`}
          />
          <Tab
            style={{
              borderBottom: '2px solid rgba(0, 0, 0, 0.38)',
              textTransform: 'none',
            }}
            value={NpsSurveyTabType.DASHBOARD}
            label={t('survey.dashboard')}
            component={Link}
            to={`${url}/${NpsSurveyTabType.DASHBOARD}`}
          />
        </Tabs>
      </TabContainer>

      <Routes>
        <Route
          index
          element={<Navigate to={`${url}/${NpsSurveyTabType.SETUP}`} replace />}
        />
        <Route
          path={`${NpsSurveyTabType.SETUP}/*`}
          element={<OvNpsSurveySetupTab />}
        />
        <Route
          path={`${NpsSurveyTabType.DASHBOARD}/*`}
          element={<OvNpsSurveyDashboardTab />}
        />
      </Routes>

      {error && (
        <ErrorMessageContainer>
          <OvErrorMessage message={getAxiosErrorMessage(error)} />
        </ErrorMessageContainer>
      )}
    </Container>
  );
};

export default NpsSurvey;

const Container = styled.div`
  margin-left: 1rem;
  box-shadow: ${Variables.boxShadow.defaultBox};
  background-color: ${Colours.WHITE};
  padding: 1.5rem;
  border-radius: ${Variables.borderRadius.LARGE};
`;

const StyledPollIcon = styled(PollIcon)`
  && {
    margin-right: 0.5rem;
  }
`;

const TabContainer = styled.div`
  margin-bottom: 1.5rem;

  && {
    .MuiTabs-indicator {
      background-color: ${Colours.OV_BASE};
    }
  }
`;

const ErrorMessageContainer = styled.div`
  margin-top: 1rem;
`;
